import { activateScripts } from '@scale/utilities';

const HYDRATION_KEY = 'data-hydration-key';
const INSERTED_SCRIPTS = 'script:not(.shopify-app-block script[src])';

export function morph(oldTree, newTree, options = {}) {
  if (typeof newTree === 'string') {
    const doc = new DOMParser().parseFromString(newTree, 'text/html');
    newTree = doc.body.firstElementChild;
  }

  if (!oldTree || !newTree) return oldTree;

  if (options.hydrationMode && oldTree instanceof Element && newTree instanceof Element) {
    morphByKey(oldTree, newTree, options);
    return oldTree;
  }

  if (options.childrenOnly) {
    updateChildren(newTree, oldTree, options);
    return oldTree;
  }

  return walk(newTree, oldTree, options);
}

export const MORPH_DEFAULTS = {
  childrenOnly: true,
  hydrationMode: false,

  reject(oldNode, newNode) {
    if (newNode.nodeType === Node.TEXT_NODE && newNode.nodeValue?.trim() === '') return true;
    if (newNode.nodeType === Node.COMMENT_NODE && newNode.nodeValue === 'shopify:rendered_by_section_api') return true;
    if (
      newNode instanceof HTMLTemplateElement &&
      newNode.shadowRootMode === 'open' &&
      oldNode.parentElement?.shadowRoot
    ) return true;
    return false;
  },

  onBeforeUpdate(oldNode, newNode) {
    if (!(oldNode instanceof HTMLElement) || !(newNode instanceof HTMLElement)) return;

    for (const attr of ['data-current-checked', 'data-previous-checked', 'data-selected', 'data-active', 'data-image-arrival']) {
      const val = oldNode.getAttribute(attr);
      if (val && val !== newNode.getAttribute(attr)) {
        newNode.setAttribute(attr, val);
      }
    }

    if (oldNode.style.viewTransitionName) {
      newNode.style.viewTransitionName = oldNode.style.viewTransitionName;
    }

    if (oldNode.classList.contains('animated')) newNode.classList.add('animated');
  },

  onAfterUpdate(node) {
    if (typeof node?.updatedCallback === 'function') {
      queueMicrotask(() => node.updatedCallback());
    }
  },
};

function morphByKey(oldRoot, newRoot, options) {
  const oldMap = new Map();
  for (const el of collectKeyed(oldRoot)) {
    const key = el.getAttribute(HYDRATION_KEY);
    if (!key) continue;
    const list = oldMap.get(key) || [];
    list.push(el);
    oldMap.set(key, list);
  }

  for (const newEl of collectKeyed(newRoot)) {
    const key = newEl.getAttribute(HYDRATION_KEY);
    if (!key) continue;
    const match = oldMap.get(key)?.shift();
    if (match) morph(match, newEl, { ...options, hydrationMode: false, childrenOnly: false });
  }
}

function collectKeyed(root) {
  const targets = [];
  if (root.hasAttribute(HYDRATION_KEY)) targets.push(root);
  targets.push(...root.querySelectorAll(`[${HYDRATION_KEY}]`));
  return targets;
}

function walk(newNode, oldNode, options) {
  if (!oldNode) return newNode;
  if (!newNode) return oldNode;
  if (newNode.isSameNode?.(oldNode)) return oldNode;
  if (newNode.nodeType !== oldNode.nodeType) return newNode;

  if (newNode instanceof Element && oldNode instanceof Element) {
    if (oldNode.tagName === 'SHOPIFY-ACCELERATED-CHECKOUT-CART') return oldNode;
    if (newNode.tagName !== oldNode.tagName) return newNode;

    const nk = newNode.id, ok = oldNode.id;
    if (nk && ok && nk !== ok) return newNode;
  }

  if (oldNode instanceof Element && oldNode.hasAttribute('data-skip-node-update')) {
    updateChildren(newNode, oldNode, options);
  } else {
    updateNode(newNode, oldNode, options);
    updateChildren(newNode, oldNode, options);
  }

  options.onAfterUpdate?.(oldNode);
  return oldNode;
}

function updateNode(newNode, oldNode, options) {
  options.onBeforeUpdate?.(oldNode, newNode);

  if (newNode instanceof HTMLDetailsElement && oldNode instanceof HTMLDetailsElement) {
    if (!newNode.hasAttribute('declarative-open')) newNode.open = oldNode.open;
  }

  if (newNode instanceof HTMLDialogElement && oldNode instanceof HTMLDialogElement) {
    if (oldNode.open && !newNode.hasAttribute('open')) newNode.setAttribute('open', '');
  }

  if (newNode instanceof Element && oldNode instanceof Element) {
    if (!oldNode.isEqualNode(newNode)) copyAttributes(newNode, oldNode);
  } else if (newNode.nodeType === Node.TEXT_NODE || newNode.nodeType === Node.COMMENT_NODE) {
    if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
  }

  if (newNode instanceof HTMLInputElement && oldNode instanceof HTMLInputElement) {
    syncProperty(newNode, oldNode, 'checked');
    syncProperty(newNode, oldNode, 'disabled');
    if (newNode.indeterminate !== oldNode.indeterminate) oldNode.indeterminate = newNode.indeterminate;
    if (oldNode.type !== 'file' && newNode.value !== oldNode.value) {
      oldNode.setAttribute('value', newNode.value);
      oldNode.value = newNode.value;
    }
  } else if (newNode instanceof HTMLOptionElement && oldNode instanceof HTMLOptionElement) {
    syncProperty(newNode, oldNode, 'selected');
  } else if (newNode instanceof HTMLTextAreaElement && oldNode instanceof HTMLTextAreaElement) {
    if (newNode.value !== oldNode.value) oldNode.value = newNode.value;
  }
}

function syncProperty(src, dst, prop) {
  if (src[prop] !== dst[prop]) {
    dst[prop] = src[prop];
    src[prop] ? dst.setAttribute(prop, '') : dst.removeAttribute(prop);
  }
}

function copyAttributes(src, dst) {
  for (const { name, value, namespaceURI } of Array.from(src.attributes)) {
    if ((name === 'src' || name === 'href' || name === 'srcset') && dst.getAttribute(name) === value) continue;
    if (namespaceURI) {
      if (dst.getAttributeNS(namespaceURI, name) !== value) dst.setAttributeNS(namespaceURI, name, value);
    } else if (dst.getAttribute(name) !== value) {
      dst.setAttribute(name, value);
    }
  }

  for (const { name, namespaceURI } of Array.from(dst.attributes)) {
    if (namespaceURI ? !src.hasAttributeNS(namespaceURI, name) : !src.hasAttribute(name)) {
      namespaceURI ? dst.removeAttributeNS(namespaceURI, name) : dst.removeAttribute(name);
    }
  }
}

function updateChildren(newNode, oldNode, options) {
  if (
    oldNode instanceof Element && oldNode.hasAttribute('data-skip-subtree-update') &&
    newNode instanceof Element && newNode.hasAttribute('data-skip-subtree-update')
  ) return;

  let offset = 0;

  for (let i = 0; ; i++) {
    const oldChild = oldNode.childNodes[i];
    const newChild = newNode.childNodes[i - offset];

    if (!oldChild && !newChild) break;

    if (!newChild) {
      oldNode.removeChild(oldChild);
      i--;
      continue;
    }

    if (!oldChild) {
      oldNode.appendChild(newChild);
      activateInserted(newChild);
      offset++;
      continue;
    }

    if (same(newChild, oldChild)) {
      const morphed = walk(newChild, oldChild, options);
      if (morphed !== oldChild) {
        oldNode.replaceChild(morphed, oldChild);
        activateInserted(morphed);
        offset++;
      }
      continue;
    }

    if (options.reject?.(oldChild, newChild)) {
      newNode.removeChild(newChild);
      i--;
      continue;
    }

    let oldMatch = null;
    for (let j = i; j < oldNode.childNodes.length; j++) {
      if (same(oldNode.childNodes[j], newChild)) {
        oldMatch = oldNode.childNodes[j];
        break;
      }
    }

    if (oldMatch) {
      const morphed = walk(newChild, oldMatch, options);
      oldNode.insertBefore(morphed, oldChild);
      if (morphed !== oldMatch) {
        activateInserted(morphed);
        offset++;
      }
    } else if (!newChild.id && !oldChild.id) {
      const morphed = walk(newChild, oldChild, options);
      if (morphed !== oldChild) {
        oldNode.replaceChild(morphed, oldChild);
        activateInserted(morphed);
        offset++;
      }
    } else {
      oldNode.insertBefore(newChild, oldChild);
      activateInserted(newChild);
      offset++;
    }
  }

  if (oldNode instanceof Element) recreateAppScripts(oldNode);
}

function activateInserted(node) {
  if (node instanceof Element) activateScripts(node, INSERTED_SCRIPTS);
}

function same(a, b) {
  if (a.nodeType !== b.nodeType) return false;
  if (a instanceof Element && b instanceof Element) {
    if (a.tagName !== b.tagName) return false;
    if (a.id && b.id && a.id !== b.id) return false;
  }
  if (a.nodeType === Node.TEXT_NODE) return a.nodeValue?.trim() === b.nodeValue?.trim();
  if (a.nodeType === Node.COMMENT_NODE) return a.nodeValue === b.nodeValue;
  return true;
}

function recreateAppScripts(container) {
  for (const script of container.querySelectorAll('.shopify-app-block script[src]')) {
    const parent = script.parentElement;
    if (!parent) continue;
    const clone = document.createElement('script');
    for (const attr of script.attributes) clone.setAttribute(attr.name, attr.value);
    if (script.textContent) clone.textContent = script.textContent;
    script.remove();
    parent.appendChild(clone);
  }
}
